import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Btn, Modal, Field, inputCls, ExportButton } from "@/components/common";
import { downloadCSV, today } from "@/lib/csv";
import PassportScanner from "@/components/PassportScanner";
import { Plus, Pencil, Trash2, Users } from "lucide-react";

const empty = {
  full_name: "", passport_number: "", nationality: "", birth_date: "", sex: "",
  expiry_date: "", country: "", email: "", phone: "", notes: "", passport_image: "",
};

export default function Ospiti() {
  const [guests, setGuests] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/guests").then((r) => setGuests(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (g) => { setForm({ ...empty, ...g }); setEditId(g.id); setOpen(true); };

  const onScan = (data, dataUrl) => {
    setForm((f) => ({
      ...f,
      full_name: data.full_name || f.full_name,
      passport_number: data.passport_number || f.passport_number,
      nationality: data.nationality || f.nationality,
      birth_date: data.birth_date || f.birth_date,
      sex: data.sex || f.sex,
      expiry_date: data.expiry_date || f.expiry_date,
      country: data.country || f.country,
      passport_image: dataUrl || f.passport_image,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/guests/${editId}`, form);
      else await api.post("/guests", form);
      toast.success("Ospite salvato");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare l'ospite?")) return;
    await api.delete(`/guests/${id}`);
    load();
  };

  const filtered = guests.filter((g) =>
    `${g.full_name || ""} ${g.name || ""} ${g.surname || ""} ${g.passport_number || ""}`.toLowerCase().includes(q.toLowerCase())
  );

  const exportCSV = () => {
    const headers = ["Cognome", "Nome", "Passaporto", "Nazionalità", "Nascita", "Sesso", "Scadenza", "Paese", "Email", "Telefono"];
    const rows = filtered.map((g) => [
      g.surname, g.name, g.passport_number, g.nationality, g.birth_date, g.sex, g.expiry_date, g.country, g.email, g.phone,
    ]);
    downloadCSV(`ospiti_${today()}.csv`, headers, rows);
  };

  return (
    <div>
      <PageHeader title="Ospiti" subtitle="Anagrafica ospiti con scansione passaporto AI">
        <input
          data-testid="guest-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca ospite…"
          className="px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-sky-500 bg-white"
        />
        <ExportButton onClick={exportCSV} testid="export-guests" />
        <Btn data-testid="new-guest-btn" onClick={openNew}><Plus size={16} /> Nuovo ospite</Btn>
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="px-3 py-2.5">Ospite</th>
              <th className="px-3 py-2.5">Passaporto</th>
              <th className="px-3 py-2.5">Nazionalità</th>
              <th className="px-3 py-2.5">Nascita</th>
              <th className="px-3 py-2.5">Scadenza</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody data-testid="guests-table">
            {filtered.map((g) => (
              <tr key={g.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2.5 font-medium text-slate-800">{g.full_name || `${g.name} ${g.surname}`}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    {g.passport_image && <img src={g.passport_image} alt="pp" className="w-9 h-7 object-cover rounded border border-slate-200" />}
                    <span className="text-slate-600 font-mono text-xs">{g.passport_number || "—"}</span>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-slate-600">{g.nationality || "—"}</td>
                <td className="px-3 py-2.5 text-slate-600">{g.birth_date || "—"}</td>
                <td className="px-3 py-2.5 text-slate-600">{g.expiry_date || "—"}</td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => openEdit(g)} className="p-1.5 text-slate-400 hover:text-sky-600"><Pencil size={15} /></button>
                    <button data-testid={`del-guest-${g.id}`} onClick={() => remove(g.id)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-12 text-center text-slate-400"><Users className="mx-auto mb-2" /> Nessun ospite</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifica ospite" : "Nuovo ospite"} wide>
        <form onSubmit={submit} className="space-y-4" data-testid="guest-form">
          <PassportScanner onResult={onScan} />
          {form.passport_image && (
            <img src={form.passport_image} alt="passaporto" className="h-28 rounded border border-slate-200 object-contain" />
          )}
          <Field label="Nome completo">
            <input data-testid="g-fullname" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Numero passaporto">
              <input data-testid="g-passport" value={form.passport_number} onChange={(e) => set("passport_number", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Nazionalità">
              <input data-testid="g-nationality" value={form.nationality} onChange={(e) => set("nationality", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Data di nascita">
              <input type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Sesso (M/F)">
              <input value={form.sex} onChange={(e) => set("sex", e.target.value)} className={inputCls} maxLength={1} />
            </Field>
            <Field label="Scadenza documento">
              <input type="date" value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Paese">
              <input value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Email">
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Telefono">
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
            </Field>
          </div>
          <Field label="Note">
            <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Annulla</Btn>
            <Btn type="submit" data-testid="save-guest">Salva ospite</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
